/**
 * @fileoverview categoryWrappers - Simplified category operation wrappers.
 *
 * Creates simplified wrapper functions that hide the complexity of passing
 * form state to category utility functions. This makes the API cleaner for
 * components using the useTransactions hook.
 *
 * @description These wrappers:
 * - Pre-bind form state to category utility functions
 * - Provide simpler APIs (e.g., addCategoryRow() instead of addCategoryRow(cats, setCats))
 * - Automatically route to correct form state (add form vs edit form)
 * - Make component code cleaner and easier to understand
 *
 * Pattern:
 * Instead of: categoryUtils.addCategoryRow(transactionCategories, setTransactionCategories)
 * Components call: addCategoryRow() - state is already bound
 *
 * @module hooks/helpers/categoryWrappers
 */

/**
 * Creates simplified category operation wrappers.
 *
 * @param {Object} categoryUtils - Category utilities from useTransactionCategories
 * @param {Object} formState - Add form state from useTransactionForm
 * @param {Object} editState - Edit form state from useTransactionEdit
 * @returns {Object} Wrapper functions with pre-bound state
 * @returns {Function} return.addCategoryRow - Add row to add form
 * @returns {Function} return.removeCategoryRow - Remove row from add form
 * @returns {Function} return.updateTransactionCategory - Update category in add form
 * @returns {Function} return.addEditCategoryRow - Add row to edit form
 * @returns {Function} return.removeEditCategoryRow - Remove row from edit form
 * @returns {Function} return.updateEditTransactionCategory - Update category in edit form
 * @returns {Function} return.getAvailableCategories - Get available categories for dropdown
 * @returns {Function} return.getAvailableCategoriesForExpenses - Get categories with allowExpense=true
 * @returns {Function} return.getAvailableCategoriesForTransfers - Get categories with allowTransfer=true
 * @returns {Function} return.isGrandfatheredCategory - Check if category is grandfathered
 *
 * @example
 * const wrappers = createCategoryWrappers(categoryUtils, formState, editState);
 * wrappers.addCategoryRow(); // Automatically uses formState
 * wrappers.addEditCategoryRow(); // Automatically uses editState
 */
export const createCategoryWrappers = (categoryUtils, formState, editState) => {
    const addCategoryRow = () => {
        categoryUtils.addCategoryRow(
            formState.transactionCategories,
            formState.setTransactionCategories
        );
    };

    const removeCategoryRow = (index) => {
        categoryUtils.removeCategoryRow(
            index,
            formState.transactionCategories,
            formState.setTransactionCategories
        );
    };

    const updateTransactionCategory = (index, field, value) => {
        categoryUtils.updateTransactionCategory(
            index,
            field,
            value,
            formState.transactionCategories,
            formState.setTransactionCategories,
            formState.amount // Pass total amount for auto-adjustment
        );
    };

    const addEditCategoryRow = () => {
        categoryUtils.addEditCategoryRow(
            editState.editTransactionCategories,
            editState.setEditTransactionCategories
        );
    };

    const removeEditCategoryRow = (index) => {
        categoryUtils.removeEditCategoryRow(
            index,
            editState.editTransactionCategories,
            editState.setEditTransactionCategories
        );
    };

    const updateEditTransactionCategory = (index, field, value) => {
        categoryUtils.updateEditTransactionCategory(
            index,
            field,
            value,
            editState.editTransactionCategories,
            editState.setEditTransactionCategories,
            editState.editAmount // Pass total amount for auto-adjustment
        );
    };

    const getAvailableCategories = (currentIndex, isEdit = false) => {
        return categoryUtils.getAvailableCategories({
            currentIndex,
            isEdit,
            transactionCategories: formState.transactionCategories,
            editTransactionCategories: editState.editTransactionCategories,
            date: formState.date,
            editDate: editState.editDate
        });
    };

    const getAvailableCategoriesForExpenses = (currentIndex, isEdit = false) => {
        return categoryUtils.getAvailableCategoriesForExpenses({
            currentIndex,
            isEdit,
            transactionCategories: formState.transactionCategories,
            editTransactionCategories: editState.editTransactionCategories,
            date: formState.date,
            editDate: editState.editDate,
            initialEditCategories: editState.initialEditCategories
        });
    };

    const getAvailableCategoriesForTransfers = (currentIndex, isEdit = false) => {
        return categoryUtils.getAvailableCategoriesForTransfers({
            currentIndex,
            isEdit,
            transactionCategories: formState.transactionCategories,
            editTransactionCategories: editState.editTransactionCategories,
            date: formState.date,
            editDate: editState.editDate,
            initialEditCategories: editState.initialEditCategories
        });
    };

    const isGrandfatheredCategory = (categoryName, isEdit = false) => {
        const transactionType = isEdit ? editState.editTransactionType : formState.transactionType;
        const transactionDate = isEdit ? editState.editDate : formState.date;
        return categoryUtils.isGrandfatheredCategory(categoryName, transactionType, transactionDate);
    };

    return {
        addCategoryRow,
        removeCategoryRow,
        updateTransactionCategory,
        addEditCategoryRow,
        removeEditCategoryRow,
        updateEditTransactionCategory,
        getAvailableCategories,
        getAvailableCategoriesForExpenses,
        getAvailableCategoriesForTransfers,
        isGrandfatheredCategory
    };
};
